import React, { useState, useEffect } from 'react';
import './AddRequisiteModal.css';
import DetailsPortal from './DetailsPortal';

const getDefaultFormData = () => ({
  login: '',
  password: '',
  name: '',
  role: 'trader',
  gatewayId: '',
  status: 'active',
  wallet: '',
  traffic: 'off',
  markupCoefficient: '',
  payInCoefficient: '',
  less500PayInCoefficient: '',
  payOutCoefficient: '',
  minFiatPayIn: '',
  maxFiatPayIn: '',
  minFiatPayOut: '',
  maxFiatPayOut: ''
});

const getRoleFieldVisibility = (role) => ({
  showGatewayId: role === 'gateway',
  showTraderFields: role === 'trader'
});

const normalizeTraffic = (value) => {
  if (value === undefined || value === null || value === '') return 'off';

  return ['off', 'false', '0', 'disabled'].includes(String(value).trim().toLowerCase())
    ? 'off'
    : 'on';
};

const AddTraderModal = ({ isOpen, onClose, onSubmit, initialData = null, isEdit = false }) => {
  const [formData, setFormData] = useState(getDefaultFormData());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showGatewayId, showTraderFields } = getRoleFieldVisibility(formData.role);

  // Fill form with data when editing
  useEffect(() => {
    if (isOpen && initialData && isEdit) {
      setFormData({
        ...getDefaultFormData(),
        login: initialData.login || initialData.Login || initialData.LOGIN || '',
        password: '', // Don't pre-fill password
        name: initialData.name || initialData.Name || initialData.NAME || '',
        role: initialData.role || initialData.Role || initialData.ROLE || 'trader',
        gatewayId: initialData.gatewayId || initialData.GatewayId || initialData.GATEWAY_ID || initialData.id || initialData.Id || initialData.ID || '',
        status: initialData.status || initialData.Status || initialData.STATUS || 'active',
        wallet: initialData.wallet || initialData.Wallet || initialData.WALLET || '',
        traffic: normalizeTraffic(initialData.traffic ?? initialData.Traffic ?? initialData.TRAFFIC),
        markupCoefficient: (
          initialData.markupCoefficient ??
          initialData.MarkupCoefficient ??
          initialData.MARKUP_COEFFICIENT ??
          ''
        ),
        payInCoefficient: (
          initialData.payInCoefficient ??
          initialData.PayInCoefficient ??
          initialData.PAYIN_COEFFICIENT ??
          ''
        ),
        less500PayInCoefficient: (
          initialData.less500PayInCoefficient ??
          initialData.Less500PayInCoefficient ??
          initialData.LESS500PayInCoefficient ??
          initialData.LESS500_PAYIN_COEFFICIENT ??
          initialData.LESS500_PAY_IN_COEFFICIENT ??
          ''
        ),
        payOutCoefficient: (
          initialData.payOutCoefficient ??
          initialData.PayOutCoefficient ??
          initialData.PAYOUT_COEFFICIENT ??
          ''
        ),
        minFiatPayIn: initialData.minFiatPayIn || initialData.MinFiatPayIn || initialData.MIN_FIAT_PAYIN || '',
        maxFiatPayIn: initialData.maxFiatPayIn || initialData.MaxFiatPayIn || initialData.MAX_FIAT_PAYIN || '',
        minFiatPayOut: initialData.minFiatPayOut || initialData.MinFiatPayOut || initialData.MIN_FIAT_PAYOUT || '',
        maxFiatPayOut: initialData.maxFiatPayOut || initialData.MaxFiatPayOut || initialData.MAX_FIAT_PAYOUT || ''
      });
    } else if (isOpen && !isEdit) {
      setFormData(getDefaultFormData());
    }
  }, [isOpen, initialData, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validation
      if (!formData.login || !formData.name) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Password is required only when creating
      if (!isEdit && !formData.password) {
        setError('Password is required');
        setLoading(false);
        return;
      }

      if (formData.role === 'gateway' && !formData.gatewayId.trim()) {
        setError('Gateway ID is required for gateway accounts');
        setLoading(false);
        return;
      }

      const submissionData = {
        ...formData,
        gatewayId: showGatewayId ? formData.gatewayId : '',
        wallet: showTraderFields ? formData.wallet : '',
        traffic: showTraderFields ? formData.traffic : '',
        markupCoefficient: showTraderFields ? formData.markupCoefficient : '',
        payInCoefficient: showTraderFields ? formData.payInCoefficient : '',
        less500PayInCoefficient: showTraderFields ? formData.less500PayInCoefficient : '',
        payOutCoefficient: showTraderFields ? formData.payOutCoefficient : '',
        minFiatPayIn: showTraderFields ? formData.minFiatPayIn : '',
        maxFiatPayIn: showTraderFields ? formData.maxFiatPayIn : '',
        minFiatPayOut: showTraderFields ? formData.minFiatPayOut : '',
        maxFiatPayOut: showTraderFields ? formData.maxFiatPayOut : ''
      };

      // When editing, pass original data as well
      const result = isEdit 
        ? await onSubmit(submissionData, initialData)
        : await onSubmit(submissionData);
      
      if (result.success) {
        // Clear form and close modal after successful response
        setFormData(getDefaultFormData());
        setLoading(false);
        onClose();
      } else {
        setError(result.error || (isEdit ? 'Failed to update account' : 'Failed to create account'));
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(getDefaultFormData());
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <DetailsPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Account' : 'Add Account'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="requisite-form">
          <div className="form-group">
            <label htmlFor="login">
              Login <span className="required">*</span>
            </label>
            <input
              type="text"
              id="login"
              name="login"
              value={formData.login}
              onChange={handleInputChange}
              required
              disabled={loading || isEdit}
              className="form-input"
            />
            {isEdit && (
              <span style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                Login cannot be changed
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password {!isEdit && <span className="required">*</span>}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required={!isEdit}
              disabled={loading}
              className="form-input"
              placeholder={isEdit ? 'Leave empty to keep current password' : 'Enter password'}
            />
            {isEdit && (
              <span style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                Leave empty to keep current password
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="name">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="role">
              Role <span className="required">*</span>
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            >
              <option value="trader">Trader</option>
              <option value="gateway">Gateway</option>
              <option value="support">Support</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {showGatewayId && (
            <div className="form-group">
              <label htmlFor="gatewayId">
                Gateway ID
              </label>
              <input
                type="text"
                id="gatewayId"
                name="gatewayId"
                value={formData.gatewayId}
                onChange={handleInputChange}
                disabled={loading}
                className="form-input"
                placeholder="Required for gateway users"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="status">
              Status <span className="required">*</span>
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              disabled={loading}
              className="form-input"
            >
              <option value="active">Active</option>
              <option value="offline">Offline</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {showTraderFields && (
            <>
              <div className="form-group">
                <label htmlFor="wallet">
                  Wallet
                </label>
                <input
                  type="text"
                  id="wallet"
                  name="wallet"
                  value={formData.wallet}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="TRON wallet address"
                />
              </div>

              <div className="form-group">
                <span className="form-label" id="traffic-label">
                  Traffic
                </span>
                <button
                  type="button"
                  className={`traffic-switch ${formData.traffic === 'on' ? 'is-on' : ''}`}
                  role="switch"
                  aria-checked={formData.traffic === 'on'}
                  aria-labelledby="traffic-label"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      traffic: prev.traffic === 'on' ? 'off' : 'on'
                    }));
                    setError(null);
                  }}
                  disabled={loading}
                >
                  <span className="traffic-switch-track" aria-hidden="true">
                    <span className="traffic-switch-thumb" />
                  </span>
                  <span className="traffic-switch-value">
                    {formData.traffic === 'on' ? 'On' : 'Off'}
                  </span>
                </button>
              </div>

              <div className="form-group">
                <label htmlFor="markupCoefficient">
                  Markup Coefficient
                </label>
                <input
                  type="number"
                  step="0.0001"
                  id="markupCoefficient"
                  name="markupCoefficient"
                  value={formData.markupCoefficient}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="1.3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="payInCoefficient">
                  Pay In Coefficient
                </label>
                <input
                  type="number"
                  step="0.0001"
                  id="payInCoefficient"
                  name="payInCoefficient"
                  value={formData.payInCoefficient}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="e.g. 1.02"
                />
              </div>

              <div className="form-group">
                <label htmlFor="less500PayInCoefficient">
                  LESS500 Pay In Coefficient
                </label>
                <input
                  type="number"
                  step="0.0001"
                  id="less500PayInCoefficient"
                  name="less500PayInCoefficient"
                  value={formData.less500PayInCoefficient}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="e.g. 1.02"
                />
              </div>

              <div className="form-group">
                <label htmlFor="payOutCoefficient">
                  Pay Out Coefficient
                </label>
                <input
                  type="number"
                  step="0.0001"
                  id="payOutCoefficient"
                  name="payOutCoefficient"
                  value={formData.payOutCoefficient}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="e.g. 0.98"
                />
              </div>

              <div className="form-group">
                <label htmlFor="minFiatPayIn">
                  Min Fiat Pay In
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="minFiatPayIn"
                  name="minFiatPayIn"
                  value={formData.minFiatPayIn}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="e.g. 500"
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxFiatPayIn">
                  Max Fiat Pay In
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="maxFiatPayIn"
                  name="maxFiatPayIn"
                  value={formData.maxFiatPayIn}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="e.g. 100000"
                />
              </div>

              <div className="form-group">
                <label htmlFor="minFiatPayOut">
                  Min Fiat Pay Out
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="minFiatPayOut"
                  name="minFiatPayOut"
                  value={formData.minFiatPayOut}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="e.g. 500"
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxFiatPayOut">
                  Max Fiat Pay Out
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="maxFiatPayOut"
                  name="maxFiatPayOut"
                  value={formData.maxFiatPayOut}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="form-input"
                  placeholder="e.g. 100000"
                />
              </div>
            </>
          )}

          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
          </form>
        </div>
      </div>
    </DetailsPortal>
  );
};

export default AddTraderModal;
